import axios, { AxiosInstance, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://shipx.gxpresss.com/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    const message = error.response?.data?.error || error.response?.data?.message || error.message || 'An error occurred';
    toast.error(message);
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: { username: string; password: string }) =>
    api.post('/auth/login/', credentials),
  
  
  logout: () =>
    api.post('/auth/logout/'),
  
  getUserInfo: () =>
    api.get('/auth/user-info/'),
  
  getProfile: () =>
    api.get('/auth/profile/'),
  
  updateProfile: (profileData: any) =>
    api.patch('/auth/profile/', profileData),
  
  changePassword: (passwordData: any) =>
    api.post('/auth/change-password/', passwordData),
  
  requestPasswordReset: (email: string) =>
    api.post('/auth/password-reset/', { email }),
  
  confirmPasswordReset: (data: any) =>
    api.post('/auth/password-reset-confirm/', data),
  
  verifyEmail: (token: string) =>
    api.post('/auth/verify-email/', { token }),
};

// Labels API
export const labelsAPI = {
  getLabels: (params?: any) =>
    api.get('/labels/labels/', { params }),
  
  getLabel: (id: number) =>
    api.get(`/labels/labels/${id}/`),
  
  createLabel: (labelData: any) =>
    api.post('/labels/labels/', labelData),
  
  updateLabel: (id: number, labelData: any) =>
    api.patch(`/labels/labels/${id}/`, labelData),
  
  deleteLabel: (id: number) =>
    api.delete(`/labels/labels/${id}/`),
  
  printLabel: (id: number) =>
    api.post(`/labels/labels/${id}/print_label/`, {}, {
      responseType: 'blob'
    }),
  
  shipLabel: (id: number) =>
    api.post(`/labels/labels/${id}/ship_label/`),
  
  processUpsLabel: (id: number) =>
    api.post(`/labels/labels/${id}/process_ups_label/`),
  
  bulkDownload: (labelIds: number[]) =>
    api.get('/labels/labels/bulk_download/', { 
      params: { ids: labelIds },
      responseType: 'blob'
    }),
  
  // Bulk Jobs
  getBulkJobs: (params?: any) =>
    api.get('/labels/bulk-jobs/', { params }),
  
  getBulkJob: (id: number) =>
    api.get(`/labels/bulk-jobs/${id}/`),
  
  uploadBulkFile: (file: File, serviceType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('service_type', serviceType);
    return api.post('/labels/bulk-jobs/upload_file/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  downloadBulkResults: (id: number) =>
    api.get(`/labels/bulk-jobs/${id}/download_results/`, {
      responseType: 'blob'
    }),
  
  // Addresses
  getAddresses: (params?: any) =>
    api.get('/labels/addresses/', { params }),
  
  getAddress: (id: number) =>
    api.get(`/labels/addresses/${id}/`),
  
  createAddress: (addressData: any) =>
    api.post('/labels/addresses/', addressData),
  
  updateAddress: (id: number, addressData: any) =>
    api.patch(`/labels/addresses/${id}/`, addressData),
  
  deleteAddress: (id: number) =>
    api.delete(`/labels/addresses/${id}/`),
  
  // Packages
  getPackages: (params?: any) =>
    api.get('/labels/packages/', { params }),
  
  getPackage: (id: number) =>
    api.get(`/labels/packages/${id}/`),
  
  createPackage: (packageData: any) =>
    api.post('/labels/packages/', packageData),
  
  updatePackage: (id: number, packageData: any) =>
    api.patch(`/labels/packages/${id}/`, packageData),
  
  deletePackage: (id: number) =>
    api.delete(`/labels/packages/${id}/`),
};

// Tracking API
export const trackingAPI = {
  getEvents: (params?: any) =>
    api.get('/tracking/events/', { params }),
  
  getEvent: (id: number) =>
    api.get(`/tracking/events/${id}/`),
  
  trackShipment: (trackingNumber: string) =>
    api.post('/tracking/track/', { tracking_number: trackingNumber }),
  
  getSubscriptions: (params?: any) =>
    api.get('/tracking/subscriptions/', { params }),
  
  createSubscription: (subscriptionData: any) =>
    api.post('/tracking/subscriptions/', subscriptionData),
  
  updateSubscription: (id: number, subscriptionData: any) =>
    api.patch(`/tracking/subscriptions/${id}/`, subscriptionData),
  
  deleteSubscription: (id: number) =>
    api.delete(`/tracking/subscriptions/${id}/`),
};


// Addresses API
export const addressesAPI = {
  getAddressBook: (params?: any) =>
    api.get('/addresses/book/', { params }),
  
  getAddressBookEntry: (id: number) =>
    api.get(`/addresses/book/${id}/`),
  
  createAddressBookEntry: (addressData: any) =>
    api.post('/addresses/book/', addressData),
  
  updateAddressBookEntry: (id: number, addressData: any) =>
    api.patch(`/addresses/book/${id}/`, addressData),
  
  deleteAddressBookEntry: (id: number) =>
    api.delete(`/addresses/book/${id}/`),
  
  getDefaultFrom: () =>
    api.get('/addresses/book/default_from/'),
  
  getDefaultTo: () =>
    api.get('/addresses/book/default_to/'),
  
  getValidations: (params?: any) =>
    api.get('/addresses/validations/', { params }),
  
  validateAddress: (addressData: any) =>
    api.post('/addresses/validate/', addressData),
};

export default api;
